import { Spin } from 'antd'
import { getCurrentUser } from 'aws-amplify/auth'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import type { DocumentFragment } from '~app/graph/generated/graphql'
import i18n from '~app/i18n/i18n'
import Actions from './Actions'
import DocumentList from './DocumentList'
import DocumentNavigator from './DocumentNavigator'
import { InboxFilters } from './InboxFilters'
import useAssignDocuments from './useAssignDocuments'
import useDocumentData from './useDocumentData'
import { useInboxFilterReducer } from './useInboxFilterReducer'

const styles: InlineStyles = {
  loading: {
    textAlign: 'center',
    margin: 30,
  },
}

const Home = () => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    string | undefined
  >()
  const [openedDocumentIds, setOpenedDocumentIds] = useState<Set<string>>(
    () => new Set()
  )

  const selectDocument = (id: string) => {
    setSelectedDocumentId(id)
    setOpenedDocumentIds(previous => {
      if (previous.has(id)) {
        return previous
      }
      const next = new Set(previous)
      next.add(id)
      return next
    })
  }
  const [
    {
      batchSize: requestCount,
      customerId,
      excludeCustomerId,
      excludeScanCenter,
      isFiltered: isFilteredByTagger,
      scanCenter,
    },
    {
      setBatchSize: setRequestCount,
      setCustomerId,
      setExcludeCustomerId,
      setExcludeScanCenter,
      setIsFiltered: setIsFilteredByTagger,
      setScanCenter,
    },
  ] = useInboxFilterReducer()
  const [tagger, setTagger] = useState<string | undefined>()
  const hasTagger = !!tagger

  useEffect(() => {
    getCurrentUser().then(({ username }) => setTagger(username))
  }, [])

  const { data, error, loading, refetch, variables } = useDocumentData({
    taggerFiltered: isFilteredByTagger,
    tagger,
    customerId,
    excludeCustomerId,
    excludeScanCenter,
    scanCenterId: scanCenter,
  })

  const assignDocumentsToTagger = useAssignDocuments({
    requestCount,
    where: variables.where,
    refetch,
  })

  const debouncedSetCustomerId = useRef(debounce(setCustomerId, 300)).current

  if (!tagger) {
    return (
      <div style={styles.loading}>
        <Spin size="large" />
      </div>
    )
  }
  if (error) {
    const { message = i18n.t('home.failedToFetchDocuments') } = error
    return (
      <div style={styles.loading}>
        {i18n.t('home.somethingWentWrong')} {message}
      </div>
    )
  }

  const documents: DocumentFragment[] = data?.documents ?? []
  const selectedDocument = documents.find(
    document => document.id === selectedDocumentId
  )

  return (
    <div>
      <InboxFilters
        scancenterId={scanCenter}
        customerId={customerId}
        onSetCustomerId={debouncedSetCustomerId}
        tagger={tagger}
        documentsCount={documents.length}
        batchSize={requestCount}
        taggerFiltered={isFilteredByTagger}
        onAssignDocumentsToTagger={assignDocumentsToTagger}
        onBatchSize={setRequestCount}
        onTaggerFilter={setIsFilteredByTagger}
        excludeScanCenter={excludeScanCenter}
        excludeCustomerId={excludeCustomerId}
        onToggleExcludeCustomerId={() =>
          setExcludeCustomerId(!excludeCustomerId)
        }
        onToggleExcludeScanCenter={() =>
          setExcludeScanCenter(!excludeScanCenter)
        }
        onSetScancenterId={setScanCenter}
      />

      {loading || !data?.documents ? (
        <div style={styles.loading}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {selectedDocument && (
            <DocumentNavigator
              documents={documents}
              selectedDocument={selectedDocument}
              onClose={async () => {
                setSelectedDocumentId(undefined)
                await refetch()
              }}
              onRemoveDocumentDone={() => refetch()}
              onSelectDocument={selectDocument}
            />
          )}
          <DocumentList
            documents={documents}
            openedDocumentIds={openedDocumentIds}
            hasTagger={hasTagger}
            onSelectDocument={selectDocument}
            onDone={() => refetch()}
          />
          <Actions
            tagger={tagger}
            documents={documents}
            refetchDocuments={refetch}
          />
        </>
      )}
    </div>
  )
}

export default Home
